// packages/ui/src/components/Money.tsx
//
// Phase 8.2 Wave 1 Task 05 — Shared `<Money>` atomic component.
//
// vg-binding: CONTEXT:decisions=P8.D-186
// vg-binding: BR:business-rules=BR-8
// vg-binding: CONTEXT:UI-SPEC=§0.1
//
//  Currency Display Rule:
//   • Source amount is always USD (stored as integer cents to avoid float
//     drift on the wire). The `usd` prop is USD cents.
//   • Format: `$x,xxx.xx (EUR x,xxx.xx)` when a `ccy` is provided AND the
//     live FX hook resolves a rate (or `displayAmount` cents are passed
//     explicitly by the caller).
//   • Format: `$x,xxx.xx` only when no `ccy` is provided.
//   • Tooltip (F-01 M1 + F-02 v2):
//       `Display rate: 1 USD = {fxRate} {ccy} (live FX from
//        fetched {fetchedAt} ago — USD canonical)`
//     NO "locked at order creation" wording — charge always USD per
//     F-01 M1 (no FX lock at order time).
//   • Dev invariant (USD-only M1): refuses non-USD inputs as the
//     primary amount. Throws a dev-only invariant when `usd` is negative,
//     non-finite, or not an integer. Throws when the caller passes a
//     non-USD source (no `inputCcy` prop is exposed — the API is
//     USD-only by construction).
//
// Signature: `<Money usd={cents} ccy?="EUR" displayAmount?={cents} />`.

import type { JSX } from 'react';
import { useDisplayFx } from '../hooks/useDisplayFx.js';
import { cn } from '@printway-global/design-system';

/**
 * Locale used for the canonical USD label. Pinned to en-US so
 * Vietnamese / French / etc. admins still see `$1,250.50` and not
 * `$1.250,50` (USD-only M1).
 */
const USD_LOCALE = 'en-US';

/**
 * Map ISO-4217 currency code → locale hint used for the companion display.
 * Falls back to en-US for unknown codes (defensive against missing ICU data).
 */
const DISPLAY_LOCALE_HINT: Record<string, string> = {
  USD: 'en-US',
  EUR: 'de-DE',
  GBP: 'en-GB',
  VND: 'vi-VN',
  JPY: 'ja-JP',
  KRW: 'ko-KR',
  CNY: 'zh-CN',
  AUD: 'en-AU',
  CAD: 'en-CA',
  SGD: 'en-SG',
};

/**
 * Decimal precision per ISO-4217. Most currencies use 2 decimals; the
 * minor-unit-zero set renders no fractional digits.
 */
const DISPLAY_PRECISION: Record<string, number> = {
  USD: 2,
  EUR: 2,
  GBP: 2,
  VND: 0,
  JPY: 0,
  KRW: 0,
  CNY: 2,
  AUD: 2,
  CAD: 2,
  SGD: 2,
};

function precisionFor(code: string): number {
  return DISPLAY_PRECISION[code] ?? 2;
}

function localeFor(code: string): string {
  return DISPLAY_LOCALE_HINT[code] ?? 'en-US';
}

/**
 * Dev-only invariant. Throws in development; logs in production to keep
 * user-facing crashes minimal while still surfacing the contract breach.
 *
 * `import.meta.env.DEV` is true in Vite dev mode and during vitest; false
 * in production bundles. The lookup is guarded for non-Vite hosts (SSR).
 */
function devInvariant(condition: boolean, message: string): void {
  if (condition) return;
  let isDev = false;
  try {
    const meta = import.meta as unknown as { env?: { DEV?: boolean } };
    isDev = meta?.env?.DEV === true;
  } catch {
    /* default to non-dev */
  }
  if (isDev) {
    throw new Error(`[<Money> invariant] ${message}`);
  } else {
    // eslint-disable-next-line no-console
    console.error(`[<Money> invariant] ${message}`);
  }
}

/**
 * Format USD cents as `$x,xxx.xx` (canonical en-US locale).
 *
 * @example
 *   formatUsdCents(123456)  // "$1,234.56"
 *   formatUsdCents(0)       // "$0.00"
 */
export function formatUsdCents(usdCents: number): string {
  const dollars = usdCents / 100;
  return new Intl.NumberFormat(USD_LOCALE, {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(dollars);
}

/**
 * Format a converted amount in display currency as `EUR x,xxx.xx`.
 * The code is rendered as a prefix (example) for clarity.
 *
 * @example
 *   formatDisplayCents(112345, 'EUR')  // "EUR 1,123.45"
 *   formatDisplayCents(100000, 'VND')  // "VND 100,000" (zero-decimal)
 */
export function formatDisplayCents(cents: number, ccy: string): string {
  const precision = precisionFor(ccy);
  const divisor = Math.pow(10, precision);
  const major = cents / divisor;
  let formatted: string;
  try {
    formatted = new Intl.NumberFormat(localeFor(ccy), {
      style: 'decimal',
      minimumFractionDigits: precision,
      maximumFractionDigits: precision,
    }).format(major);
  } catch {
    // Defensive: any ICU lookup failure falls back to plain toFixed.
    formatted = major.toFixed(precision);
  }
  return `${ccy} ${formatted}`;
}

/**
 * Human-readable "X ago" for the tooltip. Coarse-grained — seconds /
 * minutes / hours / days — matches the wording
 * ("fetched {fetchedAt} ago"). No external dep (date-fns / luxon).
 */
function formatRelativeAgo(fetchedAtMs: number, nowMs: number): string {
  const diffSec = Math.max(0, Math.round((nowMs - fetchedAtMs) / 1000));
  if (diffSec < 5) return 'just now';
  if (diffSec < 60) return `${diffSec}s`;
  const diffMin = Math.round(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m`;
  const diffHr = Math.round(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h`;
  const diffDay = Math.round(diffHr / 24);
  return `${diffDay}d`;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export interface MoneyProps {
  /**
   * Source amount in **USD cents** (integer). USD is canonical
   * the component refuses non-USD primary inputs by construction (no
   * `inputCcy` prop is exposed).
   */
  usd: number;
  /**
   * Optional display currency code. When set + FX rate resolves (or
   * `displayAmount` is passed), the `({CCY} {amount})` companion is
   * appended. When unset (or set to 'USD'), only the USD value is shown.
   */
  ccy?: string;
  /**
   * Optional pre-computed display amount in **display-currency cents**.
   * Bypasses the live FX hook — useful when the caller already holds a
   * historical snapshot or wants to render a deterministic value.
   */
  displayAmount?: number;
  /** Tailwind alignment hint — defaults to inline left/start alignment. */
  align?: 'left' | 'right' | 'center';
  /** Extra Tailwind class names appended to the root span. */
  className?: string;
  /**
   * When false, suppresses the `(CCY x,xxx.xx)` companion (used by
   * compact cells where the tooltip carries the conversion). Defaults
   * to true.
   */
  showConverted?: boolean;
}

/**
 * `<Money>` — canonical money-display atom.
 *
 * Renders the USD source amount (always first); appends `(CCY x,xxx.xx)`
 * when a `ccy` is set AND a display amount is available (either via the
 * live FX hook or the `displayAmount` prop).
 *
 * Tooltip:
 *   `Display rate: 1 USD = {fxRate} {ccy} (live FX from, fetched
 *    {fetchedAt} ago — USD canonical)`
 *
 * Dev invariant (USD-only M1): refuses non-finite, negative, or
 * non-integer `usd` inputs. Throws in dev; logs in production.
 */
export function Money(props: MoneyProps): JSX.Element {
  const {
    usd,
    ccy,
    displayAmount,
    align,
    className,
    showConverted = true,
  } = props;

  // -------------------------------------------------------------------
  //  dev invariants.
  // -------------------------------------------------------------------
  devInvariant(
    Number.isFinite(usd),
    `usd must be a finite number (got ${usd})`,
  );
  devInvariant(
    Number.isInteger(usd),
    `usd must be integer cents (got ${usd})`,
  );
  devInvariant(usd >= 0, `usd must be >= 0 (BR-8 USD-only M1, got ${usd})`);

  const targetCcy = (ccy ?? 'USD').toUpperCase();
  const isUsdOnly = targetCcy === 'USD';

  // Dev invariant: USD-only M1 — caller MUST NOT pass non-USD codes that
  // imply a non-USD primary amount. The `ccy` prop is the *display*
  // currency; the primary `usd` is always USD. This guards against the
  // common mis-use of treating `ccy` as an input currency.
  if (!isUsdOnly && displayAmount != null) {
    devInvariant(
      Number.isFinite(displayAmount) && Number.isInteger(displayAmount),
      `displayAmount must be integer cents when provided (got ${displayAmount})`,
    );
  }

  const fx = useDisplayFx(targetCcy);

  // -------------------------------------------------------------------
  // Canonical USD label (always rendered first).
  // -------------------------------------------------------------------
  const usdLabel = formatUsdCents(usd);

  // -------------------------------------------------------------------
  // Display-currency companion (rendered only when ccy + amount resolve).
  // -------------------------------------------------------------------
  // Resolve effective display cents: explicit `displayAmount` wins; else
  // compute from the live FX rate (USD major × rate × 10^precision).
  let effectiveDisplayCents: number | null = null;
  if (!isUsdOnly && showConverted) {
    if (typeof displayAmount === 'number' && Number.isFinite(displayAmount)) {
      effectiveDisplayCents = Math.round(displayAmount);
    } else if (fx.fxRate != null) {
      const displayMajor = (usd / 100) * fx.fxRate;
      const precision = precisionFor(targetCcy);
      effectiveDisplayCents = Math.round(displayMajor * Math.pow(10, precision));
    }
  }

  const companionLabel =
    effectiveDisplayCents != null
      ? `(${formatDisplayCents(effectiveDisplayCents, targetCcy)})`
      : null;

  // -------------------------------------------------------------------
  // Tooltip
  // -------------------------------------------------------------------
  let tooltip: string;
  if (isUsdOnly) {
    tooltip = 'USD canonical (BR-8). No FX conversion applied.';
  } else if (fx.fxRate != null && fx.fetchedAt != null) {
    const ago = formatRelativeAgo(fx.fetchedAt, Date.now());
    const ratePretty = new Intl.NumberFormat(USD_LOCALE, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 6,
    }).format(fx.fxRate);
    tooltip = `Display rate: 1 USD = ${ratePretty} ${targetCcy} (live FX from P6.D-230, fetched ${ago} ago — USD canonical)`;
    if (fx.isStale) {
      tooltip += ' Rate may be stale.';
    }
  } else if (fx.errorMessage) {
    tooltip = `USD canonical (BR-8). FX lookup error: ${fx.errorMessage}.`;
  } else {
    tooltip = 'USD canonical (BR-8). Live FX from P6.D-230 — loading…';
  }

  // -------------------------------------------------------------------
  // Layout.
  // -------------------------------------------------------------------
  const alignClass =
    align === 'right'
      ? 'text-right'
      : align === 'center'
        ? 'text-center'
        : 'text-left';

  return (
    <span
      className={cn(
        'inline-flex items-baseline gap-1 tabular-nums',
        alignClass,
        className,
      )}
      data-testid="money-cell"
      data-usd-cents={usd}
      data-display-ccy={targetCcy}
      title={tooltip}
      aria-label={companionLabel ? `${usdLabel} ${companionLabel}` : usdLabel}
    >
      <span className="font-medium">{usdLabel}</span>
      {companionLabel ? (
        <span
          className={cn(
            'text-xs text-pw-text-muted',
            fx.isStale ? 'italic' : undefined,
          )}
          data-testid="money-companion"
          data-stale={fx.isStale ? 'true' : 'false'}
        >
          {companionLabel}
        </span>
      ) : null}
    </span>
  );
}

export default Money;
